import { useCallback, useEffect, useRef, useState } from 'react';

import cognitoAuthService from '../../services/cognitoAuthService';

const VERIFICATION_TIMEOUT_MS = 15000;
const VERIFIED_CONFIRMATION_MS = 1500;
const VERIFICATION_TIMEOUT_MESSAGE = 'Email verification timeout - please try again';
const VERIFICATION_FAILED_MESSAGE = 'Verification failed. Please try again.';
const RESEND_FAILED_MESSAGE = 'Failed to resend code. Please try again.';

export interface EmailCodeVerification {
  verificationError: string;
  verificationLoading: boolean;
  verificationSuccess: boolean;
  handleVerifyEmail: (code: string) => Promise<void>;
  handleResendCode: () => Promise<void>;
}

export const raceWithTimeout = <T>(
  pending: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<T> =>
  Promise.race([
    pending,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs),
    ),
  ]);

const toMessage = (caught: unknown, fallback: string): string =>
  caught instanceof Error && caught.message ? caught.message : fallback;

/**
 * Verify and resend handlers for an e-mail confirmation code. `onVerified` runs once the
 * "verified" confirmation has been on screen for a moment.
 */
export const useEmailCodeVerification = (
  email: string,
  onVerified: () => void,
): EmailCodeVerification => {
  const [verificationError, setVerificationError] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const handOffTimer = useRef<ReturnType<typeof setTimeout>>();

  // Closing during the verified pause must not hand off (and so must not sign anyone in).
  useEffect(() => () => clearTimeout(handOffTimer.current), []);

  const handleVerifyEmail = async (code: string) => {
    setVerificationLoading(true);
    setVerificationError('');

    try {
      const result = await raceWithTimeout(
        cognitoAuthService.verifyEmail({ email, code }),
        VERIFICATION_TIMEOUT_MS,
        VERIFICATION_TIMEOUT_MESSAGE,
      );

      if (result.verified) {
        setVerificationSuccess(true);
        handOffTimer.current = setTimeout(onVerified, VERIFIED_CONFIRMATION_MS);
      }
    } catch (caught: unknown) {
      setVerificationError(toMessage(caught, VERIFICATION_FAILED_MESSAGE));
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleResendCode = useCallback(async () => {
    try {
      await cognitoAuthService.resendVerificationCode(email);
    } catch (caught: unknown) {
      setVerificationError(toMessage(caught, RESEND_FAILED_MESSAGE));
      throw caught;
    }
  }, [email]);

  return {
    verificationError,
    verificationLoading,
    verificationSuccess,
    handleVerifyEmail,
    handleResendCode,
  };
};
